import { useState } from 'react';

export default function Gallery() {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState<'residential'|'commercial'>('residential');
  const [lightbox, setLightbox] = useState<string | null>(null);

  const handleMove = (clientX: number, rect: DOMRect, setter: (v: number) => void) => {
    setter(Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 2), 98));
  };

  const residentialPhotos = [
    { src: '/gallery1.jpg', caption: 'Fresh seal — curved driveway leading to garage' },
    { src: '/gallery2.jpg', caption: 'Large sealed lot — same-day job, Lakes Area' },
    { src: '/gallery3.jpg', caption: 'Wide estate driveway — sealed and ready' },
    { src: '/gallery4.jpg', caption: 'Aerial view — massive circular driveway sealed' },
    { src: '/gallery5.jpg', caption: 'Estate home with circular sealed driveway' },
    { src: '/gallery6.jpg', caption: 'Fresh winding driveway — caution tape still up' },
    { src: '/work1.jpg',    caption: 'Driveway sealcoating in progress — Walworth County' },
    { src: '/work2.jpg',    caption: 'Long sealed driveway — clean finish' },
  ];

  const commercialPhotos = [
    { src: '/striping.jpg',    caption: 'Basketball court — sealed & striped, Lakes Area' },
    { src: '/commercial1.jpg', caption: 'Large commercial lot — freshly sealed next to building' },
  ];

  return (
    <section id="gallery" className="py-24 px-6 bg-black/30">
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white text-4xl leading-none z-10"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={lightbox}
            alt="Gallery photo"
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <div className="text-orange-500 font-display font-bold text-lg uppercase tracking-widest mb-3">Our Work</div>
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white uppercase mb-4">Real Results</h2>
          <p className="text-xl text-gray-400 max-w-2xl">Every photo on this page is a real job we completed right here in Walworth County. Drag the slider to compare — click any photo to enlarge.</p>
        </div>

        {/* Single Before/After Slider */}
        <div className="mb-14">
          <h3 className="font-display text-3xl font-bold text-white uppercase mb-6 flex items-center gap-3">
            <span className="w-8 h-1 bg-orange-500 inline-block rounded"></span>
            Before & After
          </h3>
          <div
            className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden cursor-col-resize select-none shadow-2xl"
            style={{ aspectRatio: '16/9' }}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onMouseMove={e => { if (isDragging) handleMove(e.clientX, e.currentTarget.getBoundingClientRect(), setSliderPos); }}
            onTouchMove={e => handleMove(e.touches[0].clientX, e.currentTarget.getBoundingClientRect(), setSliderPos)}
          >
            <img src="/after.jpg" alt="After sealcoating" className="absolute inset-0 w-full h-full object-cover object-center" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
              <img src="/before.jpg" alt="Before sealcoating" className="absolute inset-0 h-full object-cover object-left" style={{ width: `${10000/sliderPos}%`, maxWidth: 'none' }} />
            </div>
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10" style={{ left: `${sliderPos}%` }}>
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                <i className="ri-arrow-left-right-line text-gray-800 text-lg"></i>
              </div>
            </div>
            <div className="absolute top-4 left-4 bg-black/70 text-white text-sm font-bold px-3 py-1.5 rounded-full uppercase z-10">Before</div>
            <div className="absolute top-4 right-4 bg-orange-500 text-white text-sm font-bold px-3 py-1.5 rounded-full uppercase z-10">After</div>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white/80 text-xs px-3 py-1 rounded-full z-10 pointer-events-none">← Drag to compare →</div>
          </div>
        </div>

        {/* Tabbed photo grid */}
        <div className="mb-10">
          <div className="flex gap-3 mb-8">
            {(['residential','commercial'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 rounded-lg font-bold text-sm transition-all ${activeTab === tab ? 'bg-orange-500 text-white' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}>
                {tab === 'residential' ? `Residential Jobs (${residentialPhotos.length})` : `Commercial / Striping (${commercialPhotos.length})`}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(activeTab === 'residential' ? residentialPhotos : commercialPhotos).map((photo, i) => (
              <div
                key={i}
                className="relative rounded-2xl overflow-hidden group cursor-pointer"
                style={{ aspectRatio: '4/3' }}
                onClick={() => setLightbox(photo.src)}
              >
                <img
                  src={photo.src}
                  alt={photo.caption}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                {/* Zoom icon on hover */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-black/50 rounded-full w-12 h-12 flex items-center justify-center backdrop-blur-sm">
                    <i className="ri-zoom-in-line text-white text-xl"></i>
                  </div>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white text-xs font-medium">{photo.caption}</p>
                </div>
                <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Walworth Co.</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center pt-4">
          <p className="text-gray-400 mb-6 text-lg">Want results like these for your driveway or lot?</p>
          <a href="#contact" className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-lg text-lg font-bold transition-all inline-flex items-center gap-2 shadow-lg shadow-orange-500/20">
            <i className="ri-calculator-line"></i>Get Your Free Estimate
          </a>
        </div>
      </div>
    </section>
  );
}
